import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Skeleton } from '../components/ui/skeleton';
import { reviewEditApi } from '../lib/api';
import {
  Star,
  Loader2,
  CheckCircle,
  AlertTriangle,
  Building2,
  PenLine,
  Shield,
  Clock,
  Save,
  ArrowLeft,
  XCircle,
} from 'lucide-react';

const CATEGORIES = [
  { key: 'communication', label: 'Kommunikation' },
  { key: 'service', label: 'Service' },
  { key: 'billing', label: 'Abrechnung' },
  { key: 'response_time', label: 'Reaktionszeit' },
  { key: 'competence', label: 'Kompetenz' },
];

export default function ReviewEdit() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const consumedRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [review, setReview] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [editToken, setEditToken] = useState(null);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Editable fields
  const [freeText, setFreeText] = useState('');
  const [categories, setCategories] = useState([]);

  const consumeToken = useCallback(async () => {
    if (!token) {
      setError('Kein Token angegeben. Bitte verwenden Sie den Link aus Ihrer E-Mail.');
      setLoading(false);
      return;
    }

    // Prevent double-execution in React Strict Mode
    if (consumedRef.current) return;
    consumedRef.current = true;

    try {
      const response = await reviewEditApi.consumeToken(token);
      const data = response.data;

      setReview(data.review);
      setTenant(data.tenant);
      setEditToken(data.edit_token);
      setFreeText(data.review.free_text || '');
      setVerified(data.review.verification?.email_verified || false);

      // Map categories
      if (data.review.categories && data.review.categories.length > 0) {
        setCategories(
          CATEGORIES.map((cat) => {
            const existing = data.review.categories.find((c) => c.key === cat.key);
            return {
              ...cat,
              rating: existing?.rating || 3,
              comment: existing?.comment || '',
            };
          })
        );
      } else {
        setCategories(CATEGORIES.map((c) => ({ ...c, rating: 3, comment: '' })));
      }
    } catch (err) {
      if (err.response?.status === 400) {
        setError('Dieser Link ist ungültig oder bereits verwendet worden.');
      } else {
        setError('Fehler beim Laden der Bewertung.');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    consumeToken();
  }, [consumeToken]);

  const handleCategoryRating = (key, rating) => {
    setCategories((prev) => prev.map((c) => (c.key === key ? { ...c, rating } : c)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await reviewEditApi.updateReview(
        review.id,
        {
          free_text: freeText || null,
          categories: categories.filter((c) => c.rating > 0).map((c) => ({
            key: c.key,
            label: c.label,
            rating: c.rating,
            comment: c.comment || null,
          })),
        },
        editToken
      );
      setReview(response.data.review);
      setEditMode(false);
      toast.success('Bewertung gespeichert');
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Sitzung abgelaufen. Bitte verwenden Sie den Link erneut.');
      } else {
        toast.error('Fehler beim Speichern');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    try {
      await reviewEditApi.verifyReview(review.id, editToken);
      setVerified(true);
      toast.success('Bewertung verifiziert');
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Sitzung abgelaufen. Bitte verwenden Sie den Link erneut.');
      } else {
        toast.error('Fehler beim Verifizieren');
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Bewertung endgültig löschen? Das lässt sich nicht rückgängig machen.')) return;
    setDeleting(true);
    try {
      await reviewEditApi.deleteReview(review.id, editToken);
      setDeleted(true);
    } catch (err) {
      toast.error(err.response?.status === 401
        ? 'Sitzung abgelaufen. Bitte verwenden Sie den Link erneut.'
        : 'Löschen fehlgeschlagen. Bitte später erneut versuchen.');
    } finally {
      setDeleting(false);
    }
  };

  if (deleted) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
        <Card className="max-w-md w-full card-shadow">
          <CardContent className="pt-8 pb-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h1 className="text-xl font-semibold mb-2" data-testid="review-deleted-title">Bewertung gelöscht</h1>
            <p className="text-muted-foreground mb-6">Ihre Bewertung und Ihre E-Mail-Adresse wurden entfernt.</p>
            <Link to="/"><Button variant="secondary">Zur Startseite</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full card-shadow">
          <CardHeader>
            <Skeleton className="h-6 w-48 mb-2" />
            <Skeleton className="h-4 w-72" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
        <Card className="max-w-md w-full card-shadow">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-destructive" />
            </div>
            <h1 className="text-xl font-semibold mb-2" data-testid="review-edit-error-title">
              Link nicht verfügbar
            </h1>
            <p className="text-muted-foreground mb-6" data-testid="review-edit-error-message">
              {error}
            </p>
            <Link to="/">
              <Button variant="secondary" data-testid="review-edit-back-home">
                Zur Startseite
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Verified success state
  if (verified && !editMode) {
    return (
      <div className="min-h-screen bg-muted/30" data-testid="review-edit-page">
        <header className="bg-background border-b border-border px-4 py-4">
          <div className="max-w-lg mx-auto flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-semibold">{tenant?.display_name}</p>
              <p className="text-xs text-muted-foreground">Bewertung verwalten</p>
            </div>
          </div>
        </header>

        <div className="max-w-lg mx-auto p-4 mt-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <Card className="card-shadow">
              <CardContent className="pt-8 pb-8 text-center">
                <div className="w-16 h-16 rounded-full bg-[hsl(var(--success))]/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-[hsl(var(--success))]" />
                </div>
                <h2 className="text-xl font-semibold mb-2" data-testid="review-edit-verified-title">
                  Bewertung verifiziert
                </h2>
                <p className="text-muted-foreground mb-6">
                  Ihre Bewertung wurde erfolgreich bestätigt. Vielen Dank!
                </p>
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Shield className="w-4 h-4" />
                  <span>Version {review?.edit?.version || 1}</span>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        <footer className="bg-background border-t border-border py-4 text-center mt-auto">
          <p className="text-xs text-muted-foreground">
            Powered by{' '}
            <Link to="/" className="text-primary hover:underline">
              Immoverita
            </Link>
          </p>
        </footer>
      </div>
    );
  }

  // Main view/edit state
  return (
    <div className="min-h-screen bg-muted/30" data-testid="review-edit-page">
      {/* Header */}
      <header className="bg-background border-b border-border px-4 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="font-semibold">{tenant?.display_name}</p>
            <p className="text-xs text-muted-foreground">Bewertung verwalten</p>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-lg mx-auto p-4 space-y-4 mt-4">
        {/* Status Alert */}
        <Alert className="border-[hsl(var(--accent))]/30 bg-[hsl(var(--accent))]/5">
          <PenLine className="w-4 h-4 text-[hsl(var(--accent))]" />
          <AlertTitle>Bewertung bearbeiten</AlertTitle>
          <AlertDescription>
            Sie können Ihre Bewertung ansehen, bearbeiten und abschließend verifizieren.
          </AlertDescription>
        </Alert>

        {/* Review Overview Card */}
        <Card className="card-shadow">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Ihre Bewertung</CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant={review?.path === 'positive' ? 'default' : 'secondary'}>
                  {review?.path === 'positive' ? 'Positiv' : 'Negativ'}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  <Clock className="w-3 h-3 mr-1" />v{review?.edit?.version || 1}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Overall Rating */}
            <div data-testid="review-edit-overall-rating">
              <label className="text-sm font-medium mb-2 block">Gesamtbewertung</label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <Star
                    key={rating}
                    className={`w-6 h-6 ${
                      rating <= (review?.overall_rating || 0)
                        ? 'fill-[hsl(var(--rating-gold))] text-[hsl(var(--rating-gold))]'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
                <span className="ml-2 text-sm text-muted-foreground">
                  {review?.overall_rating}/5 Sterne
                </span>
              </div>
            </div>

            {/* Categories */}
            {categories.length > 0 && (
              <div data-testid="review-edit-categories">
                <label className="text-sm font-medium mb-3 block">Kategorien</label>
                <div className="space-y-3">
                  {categories.map((category) => (
                    <div key={category.key} className="flex items-center justify-between">
                      <span className="text-sm">{category.label}</span>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <button
                            key={rating}
                            onClick={() => editMode && handleCategoryRating(category.key, rating)}
                            className={`p-0.5 ${editMode ? 'cursor-pointer' : 'cursor-default'}`}
                            disabled={!editMode}
                            data-testid={`review-edit-category-${category.key}-star-${rating}`}
                          >
                            <Star
                              className={`w-5 h-5 transition-colors duration-150 ${
                                rating <= category.rating
                                  ? 'fill-[hsl(var(--rating-gold))] text-[hsl(var(--rating-gold))]'
                                  : 'text-gray-300'
                              }`}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Free Text */}
            <div data-testid="review-edit-free-text-section">
              <label className="text-sm font-medium mb-2 block">
                {editMode ? 'Text bearbeiten' : 'Ihr Text'}
              </label>
              {editMode ? (
                <Textarea
                  value={freeText}
                  onChange={(e) => setFreeText(e.target.value)}
                  rows={4}
                  placeholder="Beschreiben Sie Ihre Erfahrung..."
                  data-testid="review-edit-free-text-input"
                />
              ) : (
                <div className="p-3 rounded-lg bg-muted text-sm min-h-[60px]" data-testid="review-edit-free-text-display">
                  {review?.free_text || (
                    <span className="text-muted-foreground italic">Kein Text angegeben</span>
                  )}
                </div>
              )}
            </div>

            {/* Rewritten Text (if exists) */}
            {review?.rewritten_text && (
              <div data-testid="review-edit-rewritten-text">
                <label className="text-sm font-medium mb-2 block">
                  Neutraler Text (automatisch generiert)
                </label>
                <div className="p-3 rounded-lg bg-[hsl(var(--accent))]/5 border border-[hsl(var(--accent))]/20 text-sm">
                  {review.rewritten_text}
                </div>
              </div>
            )}

            {/* Edit info */}
            {review?.edit?.last_edited_at && (
              <p className="text-xs text-muted-foreground">
                Zuletzt bearbeitet:{' '}
                {new Date(review.edit.last_edited_at).toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="space-y-3">
          {editMode ? (
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setEditMode(false);
                  // Reset to original
                  setFreeText(review?.free_text || '');
                  if (review?.categories?.length > 0) {
                    setCategories(
                      CATEGORIES.map((cat) => {
                        const existing = review.categories.find((c) => c.key === cat.key);
                        return { ...cat, rating: existing?.rating || 3, comment: existing?.comment || '' };
                      })
                    );
                  }
                }}
                className="flex-1"
                data-testid="review-edit-cancel-button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Abbrechen
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1"
                data-testid="review-edit-save-button"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Speichern
              </Button>
            </div>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={() => setEditMode(true)}
                className="w-full"
                data-testid="review-edit-edit-button"
              >
                <PenLine className="w-4 h-4 mr-2" />
                Bewertung bearbeiten
              </Button>
              {!verified && (
                <Button
                  onClick={handleVerify}
                  disabled={verifying}
                  className="w-full"
                  data-testid="review-edit-verify-button"
                >
                  {verifying ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Bewertung verifizieren & bestätigen
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={handleDelete}
                disabled={deleting}
                className="w-full text-destructive hover:text-destructive"
                data-testid="review-edit-delete-button"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                Bewertung löschen
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-background border-t border-border py-4 text-center mt-8">
        <p className="text-xs text-muted-foreground">
          Powered by{' '}
          <Link to="/" className="text-primary hover:underline">
            Immoverita
          </Link>
        </p>
      </footer>
    </div>
  );
}
